import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, FileSpreadsheet, FileText, Loader2, UserCircle2 } from 'lucide-react';
import { OrgReportsApi, OrgStudentReportRow } from '@/services/org-reports.api';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { formatDate } from '@/lib/format';

const columnHelper = createColumnHelper<OrgStudentReportRow>();

/** Students Report — cross-school roster for the org's own schools only (see OrgReportsService's isolation doc comment). */
export function StudentsReportPage() {
  const [schoolId, setSchoolId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const dt = useDataTable({ defaultLimit: 20 });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, classId, sectionId]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-students-report-filter'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  // Class/Section are per-school lookups (classes live in each school's own
  // schema) — same cascade shape as the Lessons report's picker, disabled
  // until a school is chosen.
  const { data: classes = [] } = useQuery({
    queryKey: ['org-students-report-classes', schoolId],
    queryFn: () => OrgPromotionApi.classesForSchool(schoolId),
    enabled: !!schoolId,
  });
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  function handleSchoolChange(v: string) {
    setSchoolId(v);
    setClassId('');
    setSectionId('');
  }

  function handleClassChange(v: string) {
    setClassId(v);
    setSectionId('');
  }

  const filterParams = {
    schoolId: schoolId || undefined,
    classId: (schoolId && classId) || undefined,
    sectionId: (schoolId && classId && sectionId) || undefined,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['org-students-report', dt.params, filterParams],
    queryFn: () => OrgReportsApi.students({ ...dt.params, ...filterParams }),
    placeholderData: keepPreviousData,
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportStudents(format, { ...filterParams, search: dt.params.search });
    } finally {
      setBusy(null);
    }
  };

  const COLUMNS = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('studentName', { header: 'Name' }),
    columnHelper.accessor('dob', { header: 'Date of Birth', cell: (ctx) => formatDate(ctx.getValue()) }),
    columnHelper.display({
      id: 'image',
      header: 'Image',
      cell: (ctx) =>
        ctx.row.original.photo ? (
          <img src={ctx.row.original.photo} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <UserCircle2 className="h-8 w-8 text-slate-300" />
        ),
    }),
    columnHelper.display({
      id: 'classSection',
      header: 'Class Section',
      cell: (ctx) => `${ctx.row.original.className} · ${ctx.row.original.sectionName}`,
    }),
    columnHelper.accessor('username', { header: 'Username', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('rollNumber', { header: 'Roll Number', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('gender', { header: 'Gender', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('schoolName', { header: 'School' }),
    columnHelper.accessor('admissionDate', { header: 'Admission Date', cell: (ctx) => formatDate(ctx.getValue()) }),
  ];

  return (
    <>
      <PageHeader
        title="Students Report"
        description="Cross-school student roster — every school in your organization, one flat list."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('csv')}
              disabled={!!busy}
              title="Export to CSV"
            >
              {busy === 'csv' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-slate-600" />}
              CSV
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
          </div>
        }
      />

      {data?.truncated && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          This organization has more students than this report can show at once — results are truncated.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="w-72 pl-9"
          />
        </div>
        <Select className="!w-56" value={schoolId} onChange={(e) => handleSchoolChange(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={classId}
          onChange={(e) => handleClassChange(e.target.value)}
          disabled={!schoolId}
          title={schoolId ? undefined : 'Select a school to filter by class'}
        >
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={sectionId}
          onChange={(e) => setSectionId(e.target.value)}
          disabled={!classId}
          title={classId ? undefined : 'Select a class to filter by section'}
        >
          <option value="">All Sections</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={COLUMNS}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No students found."
        getRowId={(row) => `${row.schoolId}:${row.admissionNo}`}
      />
    </>
  );
}
